import { useMemo } from "react";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import AddCircle from "@material-ui/icons/AddCircle";

import { Controller, useForm } from "react-hook-form";
import { useModalStyles } from "./styles";
import { createNewUserSchema, CreateUsersSchemaData } from "./schema";
import { useAttendanceLocalUserCreate } from "../../../../../../../../../services/api-tansquery/attendance-local/user/create";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "react-router-dom";
import { useAttendanceLocalDepartmentList } from "../../../../../../../../../services/api-tansquery/attendance-local/department/list";
import { AttendanceLocalUser } from "../../../../../../../../../@types/attendance-local-user";
import DepartmentField from "./components/department-field";
import UserField from "./components/user-field";

type NewUserProps = {
    open: boolean;
    existingUsers: AttendanceLocalUser[];
    handleCloser: () => void;
};

export default function NewUser({
    open,
    existingUsers,
    handleCloser,
}: NewUserProps) {
    const { slug } = useParams<{ slug: string }>();
    const { control, handleSubmit, reset } = useForm<CreateUsersSchemaData>({
        resolver: zodResolver(createNewUserSchema),
        defaultValues: {
            misto: false,
            usuario: {
                id: null,
                username: "",
                first_name: "",
                last_name: "",
            },
            setor_data: {
                setor: {
                    id: null,
                    nome: "",
                    sigla: "",
                },
            },
        },
    });

    const { data: departments } = useAttendanceLocalDepartmentList({
        unidade_slug: slug,
    });

    const { mutate: handleCreateUserFn, isLoading: loading } =
        useAttendanceLocalUserCreate();

    const { classes } = useModalStyles();

    const mistoData = useMemo(() => {
        return departments?.results?.filter((item) => item.misto);
    }, [departments]);

    const handleCreateUser = (value: CreateUsersSchemaData) => {
        const { misto, usuario, setor_data } = value;
        const hasMisto = mistoData?.length > 0;

        let setorId: number | undefined;
        if (misto && hasMisto && mistoData[0]?.setor?.id) {
            setorId = mistoData[0].setor.id;
        } else if (setor_data?.setor?.id) {
            setorId = setor_data.setor.id;
        }

        if (!setorId) {
            console.error("Setor é obrigatório");
            return;
        }

        const formatPayload: {
            cpf_user: string;
            unidade: string;
            misto?: boolean;
            setor_id: number;
        } = {
            cpf_user: usuario.username,
            unidade: slug,
            setor_id: setorId,
        };

        if (misto) {
            formatPayload.misto = misto;
        }

        handleCreateUserFn(formatPayload, {
            onSuccess: () => {
                handleCloser();
                reset();
            },
        });
    };

    function handleClose() {
        handleCloser();
        reset();
    }

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography className={classes.title}>
                <AddCircle className={classes.icon} />
                <Typography>Usuário</Typography>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <form onSubmit={handleSubmit(handleCreateUser)}>
                    {mistoData?.length === 0 && (
                        <FormControl
                            className={classes.form}
                            required
                            variant="outlined"
                            fullWidth
                        >
                            <Controller
                                name="setor_data"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <DepartmentField
                                        slug={slug}
                                        field={field}
                                        fieldState={fieldState}
                                        style={{ flex: 3 }}
                                    />
                                )}
                            />
                        </FormControl>
                    )}
                    <FormControl
                        className={classes.form}
                        required
                        variant="outlined"
                        fullWidth
                    >
                        <Controller
                            name="usuario"
                            control={control}
                            render={({ field, fieldState }) => (
                                <UserField
                                    slug={slug}
                                    field={field}
                                    fieldState={fieldState}
                                    existingUsers={existingUsers}
                                    style={{ flex: 3 }}
                                />
                            )}
                        />
                    </FormControl>
                    {mistoData?.length > 0 && (
                        <>
                            <Controller
                                name="misto"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch {...field} checked={field.value} />}
                                        label="Perfil Misto"
                                    />
                                )}
                            />
                            <FormHelperText>
                                Este usuário atua em guichê com múltiplos tipos de atendimento.
                            </FormHelperText>
                        </>
                    )}
                    <Box className={classes.contentActions}>
                        <Button
                            className={classes.cancelButton}
                            disabled={loading}
                            onClick={handleClose}
                        >
                            Cancelar
                        </Button>
                        <Button
                            className={classes.confirmButton}
                            disabled={loading}
                            variant="contained"
                            type="submit"
                        >
                            {loading ? <CircularProgress /> : "Salvar"}
                        </Button>
                    </Box>
                </form>
            </DialogContent>
        </Dialog>
    );
}
